/**
 * WEB-2058 — ЗАГОТОВКИ НОВЫХ БЛОКОВ, вынесены из хранилища редактора
 * (`editorSlice.ts`, было 4 098 строк) отдельным каталогом. Это ДАННЫЕ: какие
 * поля и значения получает блок каждого вида в момент, когда автор его
 * добавляет. Перенесены дословно (решение владельца 14.09: перенос, не
 * переписывание); хранилище по-прежнему реэкспортирует `buildNewBlockDefaults`,
 * поэтому ни один прежний импорт не сломан.
 *
 * Правило с двумя концами (WEB-1844): объявление `BlockType` ↔ явные ветки
 * здесь. Сторож рядом (`blockDefaults.web2058.guard.test.ts`) сверяет
 * множества в обе стороны и выводит их из исходников. Виды без явной ветки
 * получают общую заготовку `base` и обязаны быть названы ниже — иначе новый
 * вид блока заведётся без заготовки молча.
 */
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { BlockType, EditorBlock } from '../editorSlice';
import { generateId } from './ids';

/**
 * Виды, у которых НЕТ явной ветки: их заготовка — общая `base` (умолчания
 * полей; `id` и `type` блоку дописывает вызывающий — редьюсер `addBlock`,
 * заготовка фабрикой блока не является). Список — второй конец сторожа: вид,
 * получивший явную ветку, отсюда обязан уйти, а вид без ветки и без строки
 * здесь — красный.
 */
export const GENERIC_DEFAULT_KINDS: readonly BlockType[] = [
  'checkbox',
  'email',
  'heading',
  'hero',
  'paragraph',
  'price_breakdown_text',
  'result_screen',
  'sub_title',
  'terms_checkbox',
  'text_field',
  'title',
] as const;

/**
 * Дефолты нового блока по типу. Экспортировано ради WEB-954: облачко-превью в
 * палитре показывает ровно тот блок, который получится после клика «добавить».
 */
export function buildNewBlockDefaults(
  blockType: BlockType,
  /**
   * WEB-785 (возврат QA 2026-08-01, п.4): дефолт кнопки записывается прямо в
   * контент, поэтому локализованный фолбэк рендера его уже не спасает — автор
   * русской воронки получал английскую «Buy Now» на боевой странице. Язык
   * воронки (state.language) → строка из того же словаря publicQuizStrings.
   * Вызов без языка сохраняет прежние английские дефолты.
   */
  language?: string,
): Partial<EditorBlock> {
  const strings = getPublicQuizStrings(language);
  // WEB-1903 + WEB-1907: умолчания СОДЕРЖИМОГО (то, что увидит покупатель) —
  // на языке воронки, как и подписи кнопок выше. Русские литералы, стоявшие
  // здесь, делали английскую воронку двуязычной прямо в контенте; английские
  // (WEB-1907) — русскую и любую другую. Оба конца держит сторож
  // `editorSlice.blockDefaultsNoCyrillic.web1903`: в теле этой функции не
  // должно остаться ни кириллицы, ни многословной латинской фразы — только
  // нумераторы заготовок и технические значения.
  const defaults = strings.blockDefaults;
  const buttonTextDefault =
    blockType === 'button_next'
      ? strings.continueLabel
      : blockType === 'button_to_pay'
        ? strings.payLabel
        : blockType === 'button_link'
          ? strings.learnMoreLabel
          : blockType === 'urgency_banner'
            ? strings.claimLabel
            : blockType === 'return_to_app_button'
              ? // Ревизия пачки 15: здесь был русский хардкод при живом
                // словаре — английская воронка получала русскую подпись
                // кнопки в контент (тот же дефект, что чинили у
                // restore_purchases в ревизии пачки 13).
                strings.returnToAppFallback
              : blockType === 'restore_purchases'
                ? // Ревизия пачки 13: был русский хардкод без учёта языка —
                  // английская воронка получала русскую строку в контент.
                  strings.restorePurchasesLabel
                : blockType === 'close_webview_button'
                  ? // WEB-1212: тот же словарь, что у aria-подписи кнопки
                    // закрытия WEB-853 (closeLabel) — переиспользуем, не
                    // заводим новую строку под тот же смысл «Закрыть».
                    strings.closeLabel
                  : undefined;

  const base: Partial<EditorBlock> = {
    buttonText: buttonTextDefault,
    options:
      blockType === 'single_choice' ||
      blockType === 'multiple_choice' ||
      blockType === 'multi_step_question'
        ? [
            {
              id: generateId(),
              label:
                blockType === 'multi_step_question' ? 'Question 1' : 'Option 1',
            },
          ]
        : undefined,
    progressValue: blockType === 'progress_bar' ? 50 : undefined,
    spinnerLabel: blockType === 'spinner' ? strings.loadingLabel : undefined,
    redirectDelaySeconds: blockType === 'redirect_url' ? 3 : undefined,
    banners:
      blockType === 'payment_banners'
        ? ['visa', 'mastercard', 'apple_pay']
        : undefined,
    wallets:
      blockType === 'stripe_wallets_express'
        ? ['apple_pay', 'google_pay']
        : undefined,
  };

  // Layout & Visual defaults
  if (blockType === 'divider') {
    return {
      ...base,
      content: undefined,
      dividerStyle: 'solid',
      dividerColor: '#d9d9d9',
    };
  }
  if (blockType === 'spacer') {
    return { ...base, content: undefined, heightPx: 32 };
  }
  if (blockType === 'icon_with_text') {
    return { ...base, iconName: 'star', content: defaults.iconWithTextTitle };
  }
  if (blockType === 'bullet_list') {
    return { ...base, content: undefined, listItems: ['Item 1', 'Item 2'] };
  }
  if (blockType === 'numbered_list') {
    return { ...base, content: undefined, listItems: ['Item 1', 'Item 2'] };
  }
  if (blockType === 'accordion') {
    return {
      ...base,
      content: undefined,
      accordionItems: [{ title: 'FAQ 1', body: defaults.accordionAnswer }],
    };
  }
  if (blockType === 'columns_2') {
    return {
      ...base,
      content: undefined,
      column1Content: 'Column 1',
      column2Content: 'Column 2',
    };
  }
  if (blockType === 'card') {
    return {
      ...base,
      content: undefined,
      cardTitle: defaults.cardTitle,
      cardDescription: defaults.cardDescription,
    };
  }

  // Forms defaults
  if (blockType === 'phone_input') {
    return {
      ...base,
      content: undefined,
      placeholder: '+1 (___) ___-____',
      required: false,
    };
  }
  if (blockType === 'date_picker') {
    return {
      ...base,
      content: undefined,
      placeholder: defaults.datePickerPlaceholder,
      required: false,
    };
  }
  if (blockType === 'number_input') {
    return {
      ...base,
      content: undefined,
      placeholder: defaults.numberInputPlaceholder,
      numberMin: 0,
      numberMax: 100,
    };
  }
  if (blockType === 'select_dropdown') {
    return {
      ...base,
      content: undefined,
      selectOptions: ['Option 1', 'Option 2'],
      // Ревизия WEB-1907: подсказка пустого пункта — надпись ПОКУПАТЕЛЮ (её
      // видно в закрытом списке), поэтому язык воронки. Сами варианты
      // остаются нумераторами — их не переводим (решение ревизии пачки 3).
      placeholder: defaults.selectPlaceholder,
    };
  }
  if (blockType === 'micro_feedback') {
    return {
      ...base,
      content: defaults.microFeedbackTitle,
      feedbackDetail: defaults.microFeedbackDetail,
      feedbackTone: 'support',
    };
  }
  if (blockType === 'rating_chip') {
    return {
      ...base,
      content: undefined,
      ratingValue: 4.8,
      ratingScale: 5,
      ratingAudience: defaults.ratingAudience,
    };
  }
  if (blockType === 'likert_scale') {
    return {
      ...base,
      content: defaults.selfRelevanceQuestion,
      likertPoints: 5,
      likertMinLabel: defaults.likertDisagreeLabel,
      likertMaxLabel: defaults.likertAgreeLabel,
    };
  }
  if (blockType === 'rating_stars') {
    return {
      ...base,
      content: undefined,
      ratingLabel: defaults.ratingStarsLabel,
      ratingMax: 5,
    };
  }
  if (blockType === 'slider_range') {
    return {
      ...base,
      content: undefined,
      sliderMin: 0,
      sliderMax: 100,
      sliderStep: 1,
    };
  }
  if (blockType === 'toggle') {
    return { ...base, content: defaults.toggleLabel, defaultToggled: false };
  }
  // WEB-2158 + WEB-2162 п.3: у явного согласия ДВА текста, и оба — авторские
  // значения на языке ВОРОНКИ (словарь плеера), а не надписи кабинета. Вторая
  // галочка включена по умолчанию: клиент, который ничего не передаёт наружу,
  // выключает её сам, а вот забыть спросить согласие на передачу — дороже.
  if (blockType === 'sensitive_consent') {
    return {
      ...base,
      content: defaults.sensitiveConsentCollect,
      sensitiveShareEnabled: true,
      sensitiveShareContent: defaults.sensitiveConsentShare,
      // Замка на экран не ставим: по макету 18.09 без галочки посетителя
      // пускают дальше, а ответ просто не сохраняется.
      required: false,
    };
  }
  if (blockType === 'name_field') {
    return {
      ...base,
      content: undefined,
      placeholder: defaults.nameFieldPlaceholder,
      required: false,
    };
  }

  // Media & Social proof defaults
  if (blockType === 'video_embed') {
    // WEB-2451, решение владельца 04.11 (В84): видео, запущенное посетителем
    // кликом, по умолчанию со звуком — только у нового блока. Сохранённые блоки
    // без поля играют как раньше (без звука), миграции нет.
    return { ...base, content: undefined, videoUrl: '', videoSound: 'on' };
  }
  if (blockType === 'testimonials') {
    return {
      ...base,
      content: undefined,
      testimonialItems: [
        {
          name: defaults.testimonialName,
          text: defaults.testimonialText,
          rating: 5,
        },
      ],
    };
  }
  if (blockType === 'feature_chips') {
    // WEB-1829 → WEB-1907: заглушки на языке ВОРОНКИ — как у соседей по
    // палитре (testimonials, guarantee_badge). Довод WEB-1829 («русский
    // хардкод уехал бы покупателю в англоязычной воронке») был верен только
    // наполовину: английский хардкод ровно так же уезжал покупателю в русской.
    // Порядок чипов — порядок ключа featureChipLabels, эмодзи не переводятся.
    return {
      ...base,
      content: undefined,
      featureChipColumns: 2,
      featureChipItems: [
        { id: 'fc-1', text: defaults.featureChipLabels[0], emoji: '✨' },
        { id: 'fc-2', text: defaults.featureChipLabels[1], emoji: '📊' },
        { id: 'fc-3', text: defaults.featureChipLabels[2], emoji: '📈' },
        { id: 'fc-4', text: defaults.featureChipLabels[3], emoji: '🗂' },
      ],
    };
  }
  if (blockType === 'review_stars') {
    // WEB-3436 (решение оркестратора 16.10 вслед решению владельца о WEB-2962:
    // выдуманные числа покупателю не показываем): рейтинг и число отзывов в
    // заготовке ПУСТЫЕ — было 4.5 и 128. Пустой рейтинг скрывает блок у
    // покупателя, автор видит подсказку в превью и пустые поля в панели.
    return { ...base, content: undefined };
  }
  if (blockType === 'trust_badges') {
    return {
      ...base,
      content: undefined,
      trustBadgeItems: ['ssl', 'norton', 'mcafee'],
    };
  }
  if (blockType === 'guarantee_badge') {
    return { ...base, content: defaults.guaranteeBadge };
  }
  if (blockType === 'before_after') {
    return {
      ...base,
      content: undefined,
      beforeImageUrl: '',
      afterImageUrl: '',
    };
  }
  if (blockType === 'gallery') {
    return { ...base, content: undefined, galleryImages: [] };
  }

  // Urgency & Conversion defaults
  if (blockType === 'countdown') {
    const sevenDays = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString();
    return { ...base, content: undefined, targetDate: sevenDays };
  }
  if (blockType === 'stock_badge') {
    return { ...base, content: defaults.stockBadge };
  }
  if (blockType === 'urgency_banner') {
    return { ...base, content: defaults.urgencyBanner };
  }
  // WEB-926 defaults
  if (blockType === 'scratch_card') {
    return {
      ...base,
      content: undefined,
      scratchCoverLabel: defaults.scratchCoverLabel,
      scratchRevealTitle: defaults.scratchRevealTitle,
      scratchRevealNote: defaults.scratchRevealNote,
      scratchThresholdPercent: 45,
    };
  }
  if (blockType === 'blur_teaser') {
    return {
      ...base,
      content: defaults.blurTeaserTitle,
      blurTeaserItems: [
        {
          id: generateId(),
          label: defaults.blurTeaserFirstLabel,
          teaser: defaults.blurTeaserFirstTeaser,
          emoji: '💞',
        },
        {
          id: generateId(),
          label: defaults.blurTeaserSecondLabel,
          teaser: defaults.blurTeaserSecondTeaser,
          emoji: '⏳',
        },
      ],
      blurTeaserNote: defaults.blurTeaserNote,
    };
  }
  if (blockType === 'forecast_chart') {
    return {
      ...base,
      content: defaults.forecastTitle,
      forecastNowLabel: strings.nowGoalNowLabel,
      forecastNowValue: '',
      forecastGoalLabel: strings.nowGoalGoalLabel,
      forecastAxisStartLabel: '',
      forecastAxisEndLabel: '',
      forecastCurve: 'accelerating',
    };
  }
  if (blockType === 'promo_ticket') {
    // Дефолты намеренно НЕ утверждают, что скидка применена к заказу: такой
    // механики в системе нет (решение PM 2026-07-31), формулировку и цены
    // вписывает автор.
    return {
      ...base,
      content: undefined,
      promoTicketTitle: defaults.promoTicketTitle,
      promoTicketCode: 'PROMO50',
      promoTicketShowTimer: true,
      countdownMinutes: 15,
    };
  }
  if (blockType === 'now_goal_bars') {
    // Проценты — заготовка формы карточки (видно, что «цель» отличается от
    // «сейчас»), тексты пустые: цифры за автора мы не придумываем.
    return {
      ...base,
      content: defaults.nowGoalTitle,
      nowGoalNowLabel: strings.nowGoalNowLabel,
      nowGoalGoalLabel: strings.nowGoalGoalLabel,
      nowGoalRows: [
        {
          id: 'ngr-1',
          label: defaults.metricLabel(1),
          nowText: '',
          nowPercent: 70,
          goalText: '',
          goalPercent: 35,
        },
        {
          id: 'ngr-2',
          label: defaults.metricLabel(2),
          nowText: '',
          nowPercent: 30,
          goalText: '',
          goalPercent: 85,
        },
      ],
      nowGoalNote: '',
    };
  }
  if (blockType === 'bmi_scale') {
    // Значение ПУСТОЕ по умолчанию: чужой ИМТ на экране хуже, чем его
    // отсутствие. Подписи зон — нейтральные (никаких диагнозов), дисклеймер
    // стоит сразу, чтобы автор не публиковал шкалу без него по невнимательности.
    return {
      ...base,
      content: defaults.bmiTitle,
      bmiValue: '',
      bmiValueLabel: defaults.bmiValueLabel,
      bmiScaleMin: 15,
      bmiScaleMax: 40,
      bmiZones: [
        { id: 'bz-1', label: defaults.bmiZoneUnderweight, upTo: 18.5 },
        { id: 'bz-2', label: defaults.bmiZoneNormal, upTo: 25 },
        { id: 'bz-3', label: defaults.bmiZoneOverweight, upTo: 30 },
        { id: 'bz-4', label: defaults.bmiZoneObese, upTo: 40 },
      ],
      bmiNote: defaults.bmiNote,
    };
  }
  // WEB-926 позиции 5-6: вопросные блоки. autoAdvance ставится ЯВНО (плеер
  // дефолта не имеет — шрам field-desync WEB-780/605): смысл обоих паттернов
  // в серии экранов, которые уходят сами сразу после реакции.
  if (blockType === 'statement_agreement') {
    return {
      ...base,
      content: defaults.statementText,
      statementPrompt: defaults.selfRelevanceQuestion,
      statementNegativeLabel: defaults.statementDisagreeLabel,
      statementNeutralLabel: defaults.statementNeutralLabel,
      statementPositiveLabel: defaults.statementAgreeLabel,
      autoAdvance: true,
    };
  }
  if (blockType === 'preference_card') {
    return {
      ...base,
      content: defaults.preferenceQuestion,
      preferenceEmoji: '📕',
      preferenceCaption: '',
      preferenceNoLabel: defaults.preferenceNoLabel,
      preferenceYesLabel: defaults.preferenceYesLabel,
      autoAdvance: true,
    };
  }
  if (blockType === 'callout') {
    return { ...base, content: defaults.calloutText, calloutType: 'info' };
  }

  // Quiz-specific defaults
  if (blockType === 'progress_text') {
    return {
      ...base,
      content: undefined,
      progressCurrent: 1,
      progressTotal: 5,
      // Ревизия пачки 15: слово шага НЕ пишем в дефолт. Русское «Шаг» в
      // контенте нового блока делало английскую воронку двуязычной, а пустое
      // поле рендер закрывает сам — фолбэком `stepOf` на ЯЗЫКЕ ВОРОНКИ
      // (PublicBlockRendererExtended, ветка progress_text, WEB-785). Автор
      // по-прежнему может вписать своё слово в панели (WEB-699).
    };
  }
  if (blockType === 'conditional_branch') {
    return {
      ...base,
      content: undefined,
      branchVariable: 'answer',
      branchValue: '',
      branchNextScreenId: '',
    };
  }
  if (blockType === 'multi_step_question') {
    return { ...base, content: defaults.multiStepQuestionTitle };
  }

  // F4: new question block defaults
  if (blockType === 'image_choice') {
    return {
      ...base,
      content: undefined,
      imageChoiceOptions: [
        { id: generateId(), label: 'Option A' },
        { id: generateId(), label: 'Option B' },
      ],
      imageChoiceMultiple: false,
    };
  }
  if (blockType === 'weight_picker') {
    return {
      ...base,
      content: undefined,
      weightPickerUnitDefault: 'kg',
      weightPickerMin: 30,
      weightPickerMax: 250,
    };
  }
  if (blockType === 'height_picker') {
    return {
      ...base,
      content: undefined,
      heightPickerUnitDefault: 'cm',
      heightPickerMin: 100,
      heightPickerMax: 230,
    };
  }

  // F4: new content block defaults
  if (blockType === 'loader_processing') {
    return {
      ...base,
      content: undefined,
      loaderTitle: defaults.loaderProcessingTitle,
      // WEB-1012: зашитых стадий у нового блока больше нет. Три английские
      // строки, лежавшие здесь, и были тем «зашитым текстом», из-за которого
      // экран загрузки выглядел одинаково в любой воронке (и по-английски в
      // русской). Без них рендер строит стадии из секций самой воронки
      // (funnelSectionStages); автор по-прежнему может задать свои шаги в
      // панели — они сильнее секций.
      loaderAutoAdvance: true,
    };
  }
  if (blockType === 'chart') {
    return {
      ...base,
      content: undefined,
      chartKind: 'progress_projection',
      chartStartLabel: strings.chartStartLabel,
      chartEndLabel: strings.chartEndLabel,
    };
  }
  if (blockType === 'cookie_consent') {
    // Репорт владельца 01.09: дефолты — на языке воронки (тот же словарь и
    // мотив, что у текста кнопок WEB-785), а не английский хардкод.
    return {
      ...base,
      content: undefined,
      cookieConsentText: strings.cookieBlockMessage,
      cookieAcceptLabel: strings.consentAccept,
      cookieDeclineLabel: strings.consentDecline,
      cookiePosition: 'bottom',
    };
  }

  // F4: new input block defaults
  if (blockType === 'photo_upload') {
    return {
      ...base,
      content: undefined,
      photoUploadQuestion: defaults.photoUploadQuestion,
      photoUploadMaxSizeMb: 10,
      photoUploadAccept: ['image/*'],
    };
  }

  // F4: new paywall block defaults
  if (blockType === 'plan_selector') {
    return {
      ...base,
      content: undefined,
      planSelectorPlans: [],
      planSelectorLayout: 'cards',
    };
  }
  if (blockType === 'payment_terms') {
    return {
      ...base,
      content: undefined,
      paymentTermsTemplate: defaults.paymentTermsTemplate,
    };
  }
  if (blockType === 'downsell_popup') {
    return {
      ...base,
      content: undefined,
      downsellTriggers: ['payment_declined'],
      downsellTitle: defaults.downsellTitle,
      downsellCtaLabel: defaults.downsellCta,
    };
  }

  // WEB-1152 (П9): блоки портала отмены с удержанием.
  //
  // WEB-1905, ревизия пачки 16 (находка Ф2): подписи сюда НЕ пишутся. Раньше
  // тут лежали готовые английские строки («Your subscriptions», «Cancel
  // subscription», …) — заполненное поле глушит фолбэк по языку воронки, и
  // покупатель испанской воронки читал английские кнопки. Пусто = рендер
  // берёт слово из словаря плеера (`getPublicQuizStrings(language).portal`),
  // а панель свойств показывает его же плейсхолдером. Уже опубликованные
  // воронки не трогаем: у них поля заполнены и остаются как есть.
  if (
    blockType === 'subscription_list' ||
    blockType === 'cancel_button' ||
    // WEB-1395: возобновление подписки — симметрия cancel_button.
    blockType === 'resume_button' ||
    blockType === 'downsell_offer'
  ) {
    return { ...base, content: undefined };
  }

  /**
   * WEB-2129 п.2 — кнопка-ссылка: тот же случай «контент лежит в своих полях»
   * (ветка ниже), плюс СВОЁ умолчание вкладки. Новый блок открывается в ТОЙ ЖЕ
   * вкладке: за кнопкой-ссылкой обычно чужой пейволл, и новая вкладка рвёт
   * путь покупателя, а на телефоне ещё и прячет исходную страницу.
   *
   * У уже собранных воронок поля нет, и они остаются на прежнем `_blank` —
   * умолчание НОВОГО блока и поведение блока БЕЗ поля здесь намеренно разные
   * (пины: blockDefaults.openInNewTab / PublicBlockRenderer.openInNewTab).
   */
  if (blockType === 'button_link') {
    return { ...base, content: undefined, openInNewTab: false };
  }

  // Phase 1 / 2 blocks whose primary content lives in dedicated fields,
  // not in the generic `content` textarea — strip `content` so the
  // Properties panel does not show the redundant textarea.
  if (
    blockType === 'image' ||
    blockType === 'html' ||
    blockType === 'redirect_url' ||
    blockType === 'progress_bar' ||
    blockType === 'spinner' ||
    blockType === 'payment_banners' ||
    blockType === 'stripe_checkout_form' ||
    blockType === 'stripe_wallets_express' ||
    blockType === 'price_block' ||
    blockType === 'timer' ||
    blockType === 'return_to_app_button' ||
    blockType === 'close_webview_button'
  ) {
    return { ...base, content: undefined };
  }

  return base;
}
